import React, { useMemo } from 'react';
import { Card, Typography } from 'antd';
import styled from 'styled-components';
import tw from 'twin.macro';
import StarFilled from '@ant-design/icons/lib/icons/StarFilled';
import { Link } from 'gatsby';
import { useSelector } from 'react-redux';
import { map } from 'lodash';
import { getImageSrc } from 'logic/utils/image';
import { Stack } from 'components/stack/stack';
import { LOCATION_ICON } from 'assets/icons/icons';
import { CustomIcon } from 'components/custom-icon/CustomIcon';
import { Badge } from 'components/badge/Badge';
import { Text, Text as TextDefault } from 'components/text/Text';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { GroupWrap } from 'components/group-wrap/group-wrap';
import { OfferResponse, OffersListResponse } from 'logic/api-models/api-models';
import { getSubjectsByNumber } from 'logic/store/subject/subject.selectors';
import { TeachingLevel } from 'logic/api-models/teaching-level';
import { translateTeachingLevel } from 'logic/api-models/teaching-level.logic';
import { TutoringType } from 'logic/api-models/tutoring-type';
import { isTutoringTypeEnabled } from 'logic/api-models/tutoring-type.logic';
import { BadgePrimary } from 'components/badge/BadgePrimary';
import { mapCurrency } from 'logic/currency/map-currency';
import { USER_PROFILE_DEFAULT_AVATAR_IMAGE } from 'assets/images/images';
import { getUserNameLabel } from 'logic/utils/user-label';
import { roundToXDecimals } from 'logic/utils/number';
import { MIN_TOTAL_RATINGS } from 'logic/configs/ratings';
import { colors } from 'styles/colors';
import NoResultsFound from './NoResultsFound.inline.svg';
import {
  SearchEliteItemListPagination,
  SearchItemListPagination,
  SearchRefugeeItemListPagination,
} from './SearchItemListPagination';
import './SearchItemList.styles.less';
import { GetOfferCardTitle } from './common.logic';
import getIsTutoringSchool from 'logic/helpers/isTutoringSchool';
import { getOfferRoutePath } from 'logic/store/navigation/navigation.logic';
import { ImageCenteredContent, ImageFit } from 'src/pages-all/details/tutor/components/commons';
import { featureFlags } from 'logic/feature-flag/feature-flag';
import { Ad } from 'components/ad/ad';
import { zipAdsWithOffers, isAdPlaceHolder } from 'components/ad/ad.logic';
import { InfoVideoBox } from 'components/InfoVideoBox/InfoVideoBox';
import { ElitetutorCard } from 'src/pages-all/search/components/elitetutor-offer/elitetutor-card';
import { isElitetutor } from 'logic/helpers/tutorHelper';
import { useLocalizedI18nGlobal } from '../../../logic/translations/use-localized-i18n-global';
import { useSearchForm } from 'src/pages-all/search/components/use-search-form';
import { isCityDistrictOfBerlin } from 'logic/store/offer/search-offer/search-offer.logic';

const { Title, Paragraph } = Typography;

const { Meta } = Card;

const ItemLastRow = styled(GroupWrap)<{ maxMobileLarge: boolean }>`
  display: flex;
  flex-direction: ${({ maxMobileLarge }) => (maxMobileLarge ? 'column' : 'row')};
  justify-content: ${({ maxMobileLarge }) => (maxMobileLarge ? 'center' : '')};
`;

const ItemCost = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  color: ${colors.primary};
`;

const CardItemContainer = styled.div<{ maxMobileLarge: boolean }>`
  ${tw`flex pt-4 pb-2 px-6`};
  min-width: 100%;

  .group-wrap-item-cost {
    margin: ${({ maxMobileLarge }) => (maxMobileLarge ? '0.5rem' : '')};
    &:nth-child(2) {
      display: flex;
      justify-content: flex-end;
      flex: 1;
    }
  }
`;

const SearchItemCompRightSide: React.FC<{
  item: OfferResponse | undefined;
  getOfferTitle: GetOfferCardTitle;
  isRefugeeSearch?: boolean;
}> = ({ item, getOfferTitle, isRefugeeSearch }) => {
  useLocalizedI18nGlobal();
  const subjectsByNumber = useSelector(getSubjectsByNumber) || {};
  const teachingLevel = item?.teachingLevel as TeachingLevel;
  const teachingLevelLabel = translateTeachingLevel(teachingLevel);
  const { minTablet, maxMobileLarge } = useBreakpoints();
  {
    /* SEO Improvement Test  */
  }
  const { cityToDisplay } = useSearchForm();
  const place = (isCityDistrictOfBerlin(cityToDisplay) && cityToDisplay) || item?.location?.place;
  const offerTitle = getOfferTitle(item, subjectsByNumber, false, isRefugeeSearch);
  const isTutoringSchool = getIsTutoringSchool(item);
  const rating = isTutoringSchool
    ? item?.averageRating && roundToXDecimals(item?.averageRating)
    : item?.user?.averageRating && roundToXDecimals(item.user.averageRating);
  const totalRatings = isTutoringSchool ? item?.totalRatings : item?.user?.totalRatings;
  const shouldShowFullRating = Boolean(rating && (totalRatings || 0) >= MIN_TOTAL_RATINGS);
  const shouldShowStarOnlyRating = Boolean(
    rating &&
      (item?.user?.totalRatings || 0) > 0 &&
      (item?.user?.totalRatings || 0) < MIN_TOTAL_RATINGS
  );

  const renderRatingIfCan = () =>
    shouldShowFullRating ? (
      <div>
        <TextDefault strong>
          <StarFilled style={{ color: colors.primary, marginRight: 4 }} />
          {rating} ({totalRatings})
        </TextDefault>
      </div>
    ) : shouldShowStarOnlyRating ? (
      <div>
        <TextDefault strong>
          <StarFilled style={{ color: colors.primary, marginRight: 4 }} />
          {`${totalRatings} ${translate(
            translationKeys.searchResultsPage.common.itemList.ratings
          )}`}
        </TextDefault>
      </div>
    ) : (
      !maxMobileLarge && <div />
    );

  const renderSubjects = () => (
    <>
      {!isRefugeeSearch &&
        item?.subjects
          ?.slice(0, 3)
          .map((subjectNumber) => subjectsByNumber[subjectNumber as any])
          .map((subject, index) => <Badge key={index}>{subject?.subjectName}</Badge>)}
      {!isRefugeeSearch && (item?.subjects?.length || 0) > 3 && (
        <Badge style={{ padding: '2px 4px' }}>...</Badge>
      )}
      {isRefugeeSearch && <Badge>{translate(translationKeys.offerListCard.subjectRefugees)}</Badge>}
    </>
  );

  return (
    <CardItemContainer className="noselect" maxMobileLarge={maxMobileLarge}>
      <Stack flow="row" fullWidth>
        <Title level={2} style={{ fontSize: '18px', fontWeight: 400 }} ellipsis>
          {offerTitle}
        </Title>
        <Text size={14}>
          {!isRefugeeSearch &&
            translate(translationKeys.searchResultsPage.common.itemList.level) + ' '}
          {!isRefugeeSearch && (
            <Text size={14} ellipsis>
              {teachingLevelLabel}
            </Text>
          )}
          {!isRefugeeSearch && ' | '}
          <CustomIcon height={14} className="policy__image" src={LOCATION_ICON} /> {place}
        </Text>
        <Paragraph
          ellipsis={{
            rows: 2,
          }}
        >
          {item?.offerDesc}
        </Paragraph>
        <Stack flow="row" gap={0.5}>
          <GroupWrap gap={0.5}>{renderSubjects()}</GroupWrap>
          <GroupWrap gap={0.5}>
            {map(TutoringType).map((teachingLocation, index) => {
              const enabled = isTutoringTypeEnabled(item?.tutoringType, teachingLocation);
              return (
                <BadgePrimary key={index} color={enabled ? undefined : 'none'}>
                  {translate(translationKeys.common.teachingLocation[teachingLocation])}
                </BadgePrimary>
              );
            })}
          </GroupWrap>
          <ItemLastRow
            gap={minTablet ? 0 : 2}
            classes="group-wrap-item-cost"
            maxMobileLarge={maxMobileLarge}
          >
            {featureFlags.ratings ? renderRatingIfCan() : <div />}
            <ItemCost>
              <TextDefault color="primary" size={17} strong>
                {isRefugeeSearch
                  ? translate(translationKeys.common.voluntary)
                  : translate(translationKeys.common.costAndRate, {
                      value: item?.priceAtleast,
                      currency: mapCurrency(item?.priceCurrency),
                      time: item?.tutoringTime,
                    })}
              </TextDefault>
            </ItemCost>
          </ItemLastRow>
        </Stack>
      </Stack>
    </CardItemContainer>
  );
};

const SearchItemComp: React.FC<{
  item: OfferResponse | undefined;
  getOfferTitle: GetOfferCardTitle;
  showElite?: boolean;
  isRefugeeSearch?: boolean;
}> = ({ item, getOfferTitle, showElite, isRefugeeSearch }) => {
  const { minTablet } = useBreakpoints();
  const _isElitetutor = isElitetutor(item);

  if (_isElitetutor && showElite)
    return <ElitetutorCard item={item} getOfferTitle={getOfferTitle} />;

  return (
    <Card
      style={{ width: '100%', minWidth: '100%', margin: '0' }}
      bodyStyle={{ padding: '0', margin: '0' }}
      cover={
        !minTablet ? (
          <ImageCenteredContent
            width={'auto'}
            height={320}
            style={{
              maxHeight: '320px',
              overflow: 'hidden',
              objectFit: 'cover',
              maxWidth: '768px',
              borderRadius: '10px',
            }}
            src={getImageSrc(
              getIsTutoringSchool(item)
                ? item?.tutoringSchool?.images?.[0]?.url
                : item?.user?.imageUrl
            )}
            fallback={USER_PROFILE_DEFAULT_AVATAR_IMAGE}
            alt={getUserNameLabel(item?.user)}
            preview={false}
          />
        ) : undefined
      }
    >
      <Meta
        style={{ margin: '0' }}
        avatar={
          minTablet ? (
            <Stack justifyContent="center" alignItems="center">
              <ImageFit
                width={300}
                height={240}
                style={{ maxHeight: '300px', overflow: 'hidden' }}
                src={getImageSrc(
                  getIsTutoringSchool(item)
                    ? item?.tutoringSchool?.images?.[0]?.url
                    : item?.user?.imageUrl
                )}
                fallback={USER_PROFILE_DEFAULT_AVATAR_IMAGE}
                alt={getUserNameLabel(item?.user)}
                preview={false}
              />
            </Stack>
          ) : undefined
        }
        description={
          <SearchItemCompRightSide
            item={item}
            getOfferTitle={getOfferTitle}
            isRefugeeSearch={isRefugeeSearch}
          />
        }
      />
    </Card>
  );
};

const NoResultsWrapper = styled.div`
  ${tw`flex flex-1 items-center justify-center`};
  flex-direction: column;
`;

const InfoVideoBoxWrapper = styled.div`
  margin-top: 1133px;
`;

const SearchItemList: React.FC<{
  offers: OffersListResponse | undefined;
  getOfferTitle: GetOfferCardTitle;
  showElite?: boolean;
  explicitElitetutor?: boolean;
  isRefugeeSearch?: boolean;
}> = ({ offers, getOfferTitle, showElite, explicitElitetutor, isRefugeeSearch }) => {
  const { min400, minTablet } = useBreakpoints();
  const offersAndAdsToDisplay = zipAdsWithOffers(offers?.results);

  const offersAndAds = useMemo(
    () =>
      offersAndAdsToDisplay?.map((item, index) =>
        isAdPlaceHolder(item) ? (
          !isRefugeeSearch ? (
            <Ad key={index} adUnit={'web_between_search_results'} />
          ) : null
        ) : (
          <Link key={index} to={getOfferRoutePath(item, showElite, isRefugeeSearch)}>
            <SearchItemComp
              item={item}
              getOfferTitle={getOfferTitle}
              showElite={showElite}
              isRefugeeSearch={isRefugeeSearch}
            />
          </Link>
        )
      ),
    [offersAndAdsToDisplay]
  );

  const infoBoxText = {
    headline: translate(translationKeys.infoVideoBox.onlineTutoringPromotion.headline),
    buttonText: translate(translationKeys.infoVideoBox.onlineTutoringPromotion.buttonText),
  };

  return (
    <>
      <Stack gap={2} flow="row">
        {offersAndAdsToDisplay && offersAndAdsToDisplay.length > 0
          ? offersAndAds
          : !isRefugeeSearch && (
              <NoResultsWrapper>
                <NoResultsFound
                  width={minTablet ? 400 : min400 ? 300 : 200}
                  height={minTablet ? 430 : min400 ? 330 : 230}
                />
                <Text size={18} strong>
                  {translate(translationKeys.searchResultsPage.common.noResultsFound)}
                </Text>
                {featureFlags.eliteTutorPromotion && (
                  <InfoVideoBoxWrapper>
                    <InfoVideoBox videoID={'545088293'} text={infoBoxText} />
                  </InfoVideoBoxWrapper>
                )}
              </NoResultsWrapper>
            )}
        {explicitElitetutor ? (
          <SearchEliteItemListPagination />
        ) : isRefugeeSearch ? (
          <SearchRefugeeItemListPagination />
        ) : (
          <SearchItemListPagination />
        )}
      </Stack>
    </>
  );
};

export default SearchItemList;
